import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { useSession } from '@clerk/clerk-react';
import virtualPosService from '../../../../services/virtualPosService';
import {
  listarSuscripciones,
  contarPorEstado,
  invalidarCache,
  leerPaginaCacheada,
  FILAS_POR_PAGINA,
  ESTADOS_VIRTUALPOS,
} from '../../../../services/virtualposSuscripcionesService';
import { createClerkSupabaseClient } from '../../../../lib/supabase';
import { Button } from '../../../../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../../../components/ui/dialog';
import { Input } from '../../../../components/ui/input';
import { Label } from '../../../../components/ui/label';
import { Textarea } from '../../../../components/ui/textarea';
import { YoolTable, StatusPill, StackedCell, MonoText, RowActions, IconButton } from '../../../../components/ui/YoolTable';
import { AlertCircle, Users, RefreshCw, CheckCircle, XCircle } from 'lucide-react';
import { Alert, AlertDescription } from '../../../../components/ui/alert';
import { KpiCard } from '../../../../components/ui/KpiCard';
import { formatearRut } from '../../../../lib/rut';

const STATUS_PILL_MAP = {
  ACTIVA:              'activa',
  CANCELADA:           'cancelado',
  SUSCRIBIENDO:        'procesando',
  SUSCRIPCION_FALLIDA: 'error',
  FINALIZADA:          'cancelado',
  EXPIRADA:            'expirado',
};

const SubscriptionManager = () => {
  const navigate = useNavigate();
  // `certificados` tiene RLS y solo la deja leer al rol `authenticated`: con el
  // cliente anónimo la consulta devuelve cero filas sin error y el buscador
  // nunca encontraría un código de certificado.
  const { session } = useSession();
  // Ojo: hay que colgarse del id, no del objeto `session`. Clerk devuelve una
  // instancia nueva en cada refresh de token, y usar el objeto como dependencia
  // hacía que la pantalla se recargara sola cada pocos minutos.
  const sessionId = session?.id;
  const supabase = useMemo(() => createClerkSupabaseClient(session), [session]);

  // La página solo pide una página a la vez; el servicio decide de dónde sale
  // (VirtualPOS navegando, la base buscando).
  // Si ya se visitó la pantalla hace poco, la primera página está cacheada:
  // se pinta en el primer render en vez de mostrar el esqueleto mientras Clerk
  // resuelve la sesión.
  const cacheInicial = useMemo(() => leerPaginaCacheada({ pagina: 1 }), []);

  const [rows, setRows] = useState(cacheInicial?.filas || []);
  const [total, setTotal] = useState(cacheInicial?.total || 0);
  const [page, setPage] = useState(1);
  const [texto, setTexto] = useState('');       // lo que se está escribiendo
  const [busqueda, setBusqueda] = useState(''); // lo que ya se consultó
  const [estado, setEstado] = useState('');
  const [loading, setLoading] = useState(!cacheInicial); // esqueleto solo si no hay nada que mostrar
  const [busy, setBusy] = useState(false);      // recargas: sin vaciar la tabla
  const [error, setError] = useState(null);
  const [kpis, setKpis] = useState({ total: null, activas: null, canceladas: null });

  const [cancelling, setCancelling] = useState(false);
  const [selectedSubscription, setSelectedSubscription] = useState(null);
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [confirmText, setConfirmText] = useState('');
  const [confirmChecked, setConfirmChecked] = useState(false);

  // Descarta respuestas viejas que llegan tarde y pisarían a la última: escribir
  // rápido en el buscador dispara varias consultas seguidas.
  const consultaVigente = useRef(0);
  const buscando = busqueda.trim().length > 0;

  const cargar = useCallback(async ({ paginaPedida, textoPedido, estadoPedido, esPrimera, sinCache }) => {
    const consulta = ++consultaVigente.current;
    try {
      if (esPrimera) setLoading(true);
      setBusy(true);
      setError(null);

      const { filas, total: totalFilas, errorBd } = await listarSuscripciones({
        supabase,
        pagina: paginaPedida,
        texto: textoPedido,
        estado: estadoPedido,
        sinCache,
      });

      if (consulta !== consultaVigente.current) return; // llegó tarde
      setRows(filas);
      setTotal(totalFilas);
      if (errorBd) {
        console.error('Error leyendo la base:', errorBd);
        setError('Se cargaron las suscripciones de VirtualPOS, pero falló la lectura de la base: falta el código y los certificados asociados.');
      }
    } catch (err) {
      if (consulta !== consultaVigente.current) return;
      console.error('Error cargando suscripciones:', err);
      setError(err?.message || 'Error al cargar las suscripciones');
      setRows([]);
      setTotal(0);
    } finally {
      if (consulta === consultaVigente.current) {
        setLoading(false);
        setBusy(false);
      }
    }
  }, [supabase]);

  const cargarKpis = useCallback(async () => {
    try {
      setKpis(await contarPorEstado());
    } catch (err) {
      console.warn('No se pudieron cargar los totales:', err?.message);
    }
  }, []);

  // Debounce del buscador: no consultar en cada tecla.
  useEffect(() => {
    const t = setTimeout(() => {
      setBusqueda(texto);
      setPage(1);
    }, 400);
    return () => clearTimeout(t);
  }, [texto]);

  useEffect(() => {
    if (!sessionId) return;
    cargar({ paginaPedida: page, textoPedido: busqueda, estadoPedido: estado, esPrimera: rows.length === 0 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId, page, busqueda, estado, cargar]);

  // Los KPI son 3 llamadas más a VirtualPOS, que encola: lanzadas junto al
  // listado le robaban el turno y retrasaban las filas. Van después de que la
  // tabla ya está en pantalla — son un dato de contexto, no el contenido.
  useEffect(() => {
    if (!sessionId || loading) return;
    cargarKpis();
  }, [sessionId, loading, cargarKpis]);

  const refrescar = () => {
    cargar({ paginaPedida: page, textoPedido: busqueda, estadoPedido: estado, esPrimera: false, sinCache: true });
    cargarKpis();
  };

  // Deja la fila cancelada al día sin volver a pedir la página entera.
  const marcarSuscripcionCancelada = (subscriptionId) => {
    setRows((actuales) => actuales.map((s) => (
      String(s.id) === String(subscriptionId) ? { ...s, status: 'CANCELADA' } : s
    )));
  };

  const handleCancel = async () => {
    if (!selectedSubscription) return;
    if (confirmText !== 'CANCELAR' || !confirmChecked) return;

    const subscriptionId = selectedSubscription.id;
    try {
      setCancelling(true);
      setError(null);
      // El servicio lanza si VirtualPOS responde NOK, así que llegar aquí
      // significa que la baja quedó tomada.
      await virtualPosService.cancelSubscription(subscriptionId);

      marcarSuscripcionCancelada(subscriptionId);
      setShowCancelDialog(false);
      setCancelReason('');
      setConfirmText('');
      setConfirmChecked(false);
      setSelectedSubscription(null);
      toast.success('Suscripción cancelada');
      // La cache tiene la fila con el estado viejo: sin esto, volver a la
      // pantalla dentro del minuto la mostraría otra vez como ACTIVA.
      invalidarCache();
      cargarKpis();
      // Con un filtro por estado puesto, la fila ya no pertenece a esta página.
      if (estado) cargar({ paginaPedida: page, textoPedido: busqueda, estadoPedido: estado, esPrimera: false, sinCache: true });
    } catch (err) {
      console.error(err);
      // Mostramos el motivo que dio VirtualPOS: lo más común es intentar dar de
      // baja una que ya no está ACTIVA (E-047).
      setError(err?.message || 'Error al cancelar la suscripción');
      setShowCancelDialog(false);
    } finally {
      setCancelling(false);
    }
  };

  const formatCurrency = (amount, currency = 'CLP') => {
    if (amount == null) return '—';
    if (currency === 'UF') return `${amount} UF`;
    return new Intl.NumberFormat('es-CL', {
      style: 'currency',
      currency: currency === 'UF' ? 'CLP' : currency || 'CLP',
    }).format(amount);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '—';
    const d = new Date(dateString);
    if (Number.isNaN(d.getTime())) return '—';
    return d.toLocaleDateString('es-CL', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  const formatCount = (value) => (value == null ? '…' : new Intl.NumberFormat('es-CL').format(value));

  const porcentaje = (parte) => {
    if (!kpis.total || parte == null) return '—';
    return `${((parte / kpis.total) * 100).toFixed(1)}%`;
  };

  // Los totales vienen del `total` que informa VirtualPOS, no de contar filas:
  // con 15.000 suscripciones contar en el navegador no sería posible.
  const kpiCards = [
    { key: 'total',     title: 'Suscripciones', value: formatCount(kpis.total),      change: kpis.total == null ? '—' : '100%', trend: 'neutral', icon: Users },
    { key: 'active',    title: 'Activas',       value: formatCount(kpis.activas),    change: porcentaje(kpis.activas),          trend: 'up',      icon: CheckCircle },
    { key: 'cancelled', title: 'Canceladas',    value: formatCount(kpis.canceladas), change: porcentaje(kpis.canceladas),        trend: 'down',    icon: XCircle },
  ];

  const columns = useMemo(() => [
    {
      key: 'codigo',
      label: 'Código',
      render: (r) => <MonoText>{r._bd?.codigo || '—'}</MonoText>,
    },
    {
      key: 'client',
      label: 'Cliente',
      render: (r) => {
        // Navegando el cliente lo da VirtualPOS; buscando, la contratación.
        const nombre = `${r.client?.firstName || ''} ${r.client?.lastName || ''}`.trim()
          || (r._bd?.contratacionNombres || [])[0]
          || r._bd?.companiaNombre;
        const rutCrudo = r.client?.socialId
          || (r._bd?.contratacionRuts || [])[0]
          || r._bd?.companiaRut;
        return (
          <StackedCell
            primary={nombre || r.client?.email || '—'}
            secondary={rutCrudo ? formatearRut(rutCrudo) : (nombre ? r.client?.email : undefined)}
          />
        );
      },
    },
    {
      key: 'asociados',
      label: 'Certificado / Contratación',
      render: (r) => {
        const certificados = r._bd?.certificadoCodigos || [];
        const contrataciones = r._bd?.contratacionCodigos || [];
        if (certificados.length === 0 && contrataciones.length === 0) {
          return <span style={{ color: '#8D8D8D' }}>—</span>;
        }
        const resumir = (codigos) => {
          if (codigos.length === 0) return '—';
          return codigos.length === 1 ? codigos[0] : `${codigos[0]} +${codigos.length - 1}`;
        };
        return (
          <StackedCell
            mono
            primary={resumir(certificados)}
            secondary={resumir(contrataciones)}
          />
        );
      },
    },
    {
      key: 'planName',
      label: 'Plan',
      render: (r) => r.planName || r._bd?.planName || '—',
    },
    {
      key: 'amount',
      label: 'Monto',
      numeric: true,
      align: 'right',
      render: (r) => (
        <span style={{ fontWeight: 500 }}>{formatCurrency(r.amount, r.currency)}</span>
      ),
    },
    {
      key: 'subscriptionDate',
      label: 'Fecha',
      render: (r) => formatDate(r.subscriptionDate),
    },
    {
      key: 'status',
      label: 'Estado',
      render: (r) => {
        const dbLabel = r._bd?.status ? `BD: ${r._bd.status}` : 'BD: Sin registro';
        return (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            {r.status
              ? <StatusPill estado={STATUS_PILL_MAP[r.status] || r.status} />
              : <StatusPill estado="inactivo" tone="ring">VirtualPOS: sin dato</StatusPill>}
            <StatusPill estado="inactivo" tone="ring">{dbLabel}</StatusPill>
          </div>
        );
      },
    },
    {
      key: 'actions',
      label: '',
      align: 'right',
      render: (r) => (
        <RowActions>
          <IconButton
            icon="eye"
            title="Ver detalle"
            onClick={() => navigate(`/payments-admin/virtualpos/subscriptions/${r.id}`)}
          />
          {r.status === 'ACTIVA' && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                setSelectedSubscription(r);
                setShowCancelDialog(true);
              }}
            >
              Cancelar
            </Button>
          )}
        </RowActions>
      ),
    },
  ], [navigate]);

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="grid gap-4 md:grid-cols-3">
          {Array.from({ length: 3 }).map((_, index) => (
            <KpiCard key={`kpi-loading-${index}`} loading label="…" value="…" />
          ))}
        </div>
        <div className="flex items-center justify-center p-8">
          <RefreshCw className="h-8 w-8 animate-spin" />
          <span className="ml-2">Cargando suscripciones...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Error Alert */}
      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {/* Estadísticas básicas */}
      <div className="grid gap-4 md:grid-cols-3">
        {kpiCards.map((card) => (
          <KpiCard
            key={card.key}
            label={card.title}
            value={card.value}
            icon={card.icon}
            badge={{ text: card.change, direction: card.trend === 'up' ? 'up' : card.trend === 'down' ? 'down' : 'neu' }}
          />
        ))}
      </div>

      {/* Tabla de suscripciones */}
      <YoolTable
        columns={columns}
        rows={rows}
        rowKey="id"
        pageSize={FILAS_POR_PAGINA}
        totalRows={total}
        page={page}
        onPageChange={setPage}
        busy={busy}
        search={{ placeholder: 'Buscar por certificado, contratación, nombre, RUT o plan…' }}
        searchValue={texto}
        onSearchChange={setTexto}
        filters={[{
          key: 'status',
          label: 'Estado',
          options: ESTADOS_VIRTUALPOS.map((s) => ({ value: s, label: s })),
          // Buscando, las filas salen de la base, que no guarda el estado de
          // VirtualPOS: filtrar por él daría totales que no cuadran.
          disabled: buscando,
          title: buscando ? 'El filtro por estado no aplica mientras buscas' : undefined,
        }]}
        filterValues={{ status: estado }}
        onFilterChange={(key, value) => {
          if (key !== 'status') return;
          setEstado(value);
          setPage(1);
        }}
        toolbar={
          <Button onClick={refrescar} variant="outline" size="sm" disabled={busy}>
            <RefreshCw className={`h-4 w-4 mr-2 ${busy ? 'animate-spin' : ''}`} />
            {busy ? 'Actualizando…' : 'Actualizar'}
          </Button>
        }
        empty={buscando
          ? { title: 'Sin coincidencias', text: 'No hay suscripciones registradas en la base que coincidan con la búsqueda.' }
          : { title: 'Sin suscripciones', text: 'Aún no hay suscripciones VirtualPos registradas en el sistema.' }}
      />

      {/* Dialog para cancelar suscripción individual */}
      <Dialog open={showCancelDialog} onOpenChange={setShowCancelDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Cancelar Suscripción</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p>¿Estás seguro de que deseas cancelar esta suscripción?</p>
            {selectedSubscription && (
              <div className="bg-gray-50 p-3 rounded">
                <p><strong>Plan:</strong> {selectedSubscription.planName}</p>
                <p><strong>Cliente:</strong> {selectedSubscription.client?.email}</p>
              </div>
            )}
            <div>
              <Label htmlFor="cancelReason">Motivo de cancelación (opcional)</Label>
              <Textarea
                id="cancelReason"
                value={cancelReason}
                onChange={(e) => setCancelReason(e.target.value)}
                placeholder="Describe el motivo de la cancelación..."
              />
            </div>
            <div className="space-y-2">
              <Label>Confirmación</Label>
              <Input
                placeholder="Escribe CANCELAR"
                value={confirmText}
                onChange={(e) => setConfirmText(e.target.value)}
              />
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={confirmChecked}
                  onChange={(e) => setConfirmChecked(e.target.checked)}
                />
                Acepto cancelar permanentemente esta suscripción.
              </label>
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setShowCancelDialog(false)} disabled={cancelling}>
                Cancelar
              </Button>
              <Button
                variant="destructive"
                onClick={handleCancel}
                disabled={confirmText !== 'CANCELAR' || !confirmChecked || cancelling}
              >
                {cancelling ? 'Cancelando…' : 'Confirmar Cancelación'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

    </div>
  );
};

export default SubscriptionManager;
